
import React, { useState } from 'react';
import { trails, trailDifficulties, trailSurfaces } from '../../../mocks/parks';

export default function TrailFinder() {
  const [difficulty, setDifficulty] = useState<string>('All');
  const [surface, setSurface] = useState<string>('All');
  const [expandedTrail, setExpandedTrail] = useState<string | null>(null);

  // Defensive filtering – ensure trails is an array
  const filtered = Array.isArray(trails)
    ? trails.filter((t) => {
        const matchDiff = difficulty === 'All' || t.difficulty === difficulty;
        const matchSurf = surface === 'All' || t.surface === surface;
        return matchDiff && matchSurf;
      })
    : [];

  return (
    <section id="trails" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* Filter Sidebar */}
          <div className="lg:col-span-4">
            <div className="sticky top-40 bg-emerald-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Trail Finder</h2>
              <p className="text-gray-500 text-sm mb-6">
                Filter by difficulty, length, and surface type to find your perfect trail.
              </p>

              <div className="space-y-5">
                {/* Difficulty filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Difficulty Level
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {trailDifficulties.map((d) => (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setDifficulty(d)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          difficulty === d
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Surface filter */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Surface Type
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {trailSurfaces.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSurface(s)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                          surface === s
                            ? 'bg-emerald-500 text-white'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Result summary */}
              <div className="mt-8 p-4 bg-white rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-lg">
                    <i className="ri-route-line text-emerald-600"></i>
                  </div>
                  <span className="font-semibold text-gray-900">
                    {filtered.length} trail{filtered.length !== 1 ? 's' : ''} found
                  </span>
                </div>
                <p className="text-xs text-gray-400">Showing results based on your filters</p>
              </div>
            </div>
          </div>

          {/* Trail Cards */}
          <div className="lg:col-span-8 space-y-5">
            {filtered.length === 0 && (
              <div className="text-center py-16 bg-gray-50 rounded-2xl">
                <i className="ri-route-line text-4xl text-gray-300 mb-3"></i>
                <p className="text-gray-500">
                  No trails match your filters. Try adjusting your criteria.
                </p>
              </div>
            )}

            {filtered.map((trail) => (
              <div
                key={trail.id}
                className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all"
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image */}
                  <div className="md:w-72 h-52 md:h-auto flex-shrink-0 overflow-hidden">
                    {/* Guard against missing image URLs */}
                    {trail.image ? (
                      <img
                        src={trail.image}
                        alt={trail.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-500 text-sm">No image</span>
                      </div>
                    )}
                  </div>

                  {/* Details */}
                  <div className="flex-1 p-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{trail.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-gray-400 mt-1">
                          <i className="ri-map-pin-line"></i>
                          <span>{trail.address}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 bg-amber-50 px-2 py-1 rounded-full">
                        <i className="ri-star-fill text-amber-400 text-sm"></i>
                        <span className="text-sm font-semibold text-gray-700">{trail.rating}</span>
                        <span className="text-xs text-gray-400">({trail.reviews})</span>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-3 my-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          trail.difficulty === 'Easy'
                            ? 'bg-green-100 text-green-700'
                            : trail.difficulty === 'Moderate'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {trail.difficulty}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        <i className="ri-ruler-line mr-1"></i>
                        {trail.length}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        <i className="ri-arrow-up-down-line mr-1"></i>
                        {trail.elevation}
                      </span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                        {trail.surface}
                      </span>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 line-clamp-2 mb-3">{trail.description}</p>

                    {/* Expanded details */}
                    {expandedTrail === trail.id && (
                      <div className="mb-3">
                        <div className="flex flex-wrap gap-2">
                          {trail.amenities.map((a) => (
                            <span
                              key={a}
                              className="px-2 py-1 bg-emerald-50 text-emerald-700 text-xs rounded-full"
                            >
                              {a}
                            </span>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                          <i className="ri-time-line text-emerald-500"></i>
                          <span>{trail.hours}</span>
                        </div>
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedTrail(expandedTrail === trail.id ? null : trail.id)
                        }
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {expandedTrail === trail.id ? 'Show Less' : 'Details'}
                      </button>

                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          trail.address
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-navigation-line"></i>
                        Directions
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
